import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { giftCardApi, packageApi } from '@api';
import {
  IPackagePrice,
  IGiftCardExtendPackage,
  IGiftCardPackage,
} from '@root/types/package';
import { IOrderCreateData, IOrderExtensionData } from '@root/types/order';
import { useCurrentHouseId } from '../useHouseList';
import usePayPalResult from '../usePayPal';
import Toast from '@root/components/toast';
import { useUnBindDevicesAction } from '../useUnBindDevices';
import { EPayMode } from '@root/constant';

interface ICreateOrderProps {
  curPackage: IGiftCardPackage;
  deviceIds?: string[];
  payMode?: EPayMode;
}

const useCreateOrder = () => {
  const homeId = useCurrentHouseId();
  const { t } = useTranslation();
  const { code = '' } = useParams();
  const { getUnBindDevices } = useUnBindDevicesAction();
  const [paying, setPaying] = useState(false);
  const { getPayUrl } = usePayPalResult({ setPaying });

  // 默认订阅一份
  const onCreateOrder = useCallback(
    ({ curPackage, deviceIds, payMode }: ICreateOrderProps) => {
      setPaying(true);
      const { goodsPriceList, supportRenew, payMark } = curPackage;

      if (payMark) {
        Toast.info(t('create_order'), 0);
      } else {
        Toast.info(t('create_order_by_gift_card'), 0);
      }

      const priceInfo = goodsPriceList?.[0] || ({} as IPackagePrice);

      const price = priceInfo.price ? Number(priceInfo.price).toFixed(2) : '';

      const _data: IOrderCreateData = {
        discountRate: 0,
        currency: priceInfo.currency,
        homeId,
        deviceIds,
        goodsReqs: [
          {
            goodsId: priceInfo.goodsId,
            goodsNum: 1,
            goodsType: 5,
            goodsPriceId: priceInfo.goodsPriceId,
          },
        ],
        orderPrice: price,
        payPrice: price,
        preferentialPrice: 0,
        giftCardCode: code,
      };

      packageApi
        .createOrder(_data)
        .then((data: any) => {
          if (deviceIds?.length) {
            getUnBindDevices();
          }

          const _baseUrl = `${window.location.origin}${process.env.REACT_APP_ROUTE_BASE_NAME}`;

          getPayUrl({
            orderId: data,
            type: supportRenew,
            payMode,
            successUrl: payMark
              ? `${_baseUrl}/paySuccess`
              : `${_baseUrl}/paySuccess/free`,
          });
        })
        .catch((reason) => {
          Toast.destroy();
          setPaying(false);
          const status = reason.response?.status;
          const data = reason.response?.data || {};
          let msg = t('create_order_error');
          if (status === 400 && data.desc) {
            msg = data.desc;
          }
          Toast.info(msg);
        });
    },
    [code, getPayUrl, getUnBindDevices, homeId, t],
  );

  // 礼品卡续订
  const onCreateGiftCardExtensionOrder = useCallback(
    (curPackage: IGiftCardExtendPackage, payMode?: number) => {
      setPaying(true);
      const { goodsId, packageId, payMark } = curPackage;

      if (payMark) {
        Toast.info(t('create_order'), 0);
      } else {
        Toast.info(t('create_order_by_gift_card'), 0);
      }

      // 礼品卡续订目前没有参数确认是否需要支付 统一用创建订单的提示 是按照套餐本身是否需要付费来确定的
      // 与添财确认，可以使用 payMark 来判断是否需要支付

      const _data: IOrderExtensionData = {
        packageId,
        goodsId,
        homeId,
        giftCardCode: code,
      };

      giftCardApi
        .createGiftCardExtensionOrder(_data)
        .then((data: any) => {
          const { orderId, startTime } = data;
          const _baseUrl = `${window.location.origin}${process.env.REACT_APP_ROUTE_BASE_NAME}`;
          getPayUrl({
            orderId,
            startTime,
            type: 1,
            successUrl: `${_baseUrl}/paySuccess/free`,
            payMode,
          });
        })
        .catch((reason) => {
          setPaying(false);
          Toast.destroy();
          const status = reason.response?.status;
          const data = reason.response?.data || {};
          let msg = t('create_order_error');
          if (status === 400 && data.desc) {
            msg = data.desc;
          }
          Toast.info(msg);
        });
    },
    [code, getPayUrl, homeId, t],
  );

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    paying,
    onCreateOrder,
    onCreateGiftCardExtensionOrder,
  };
};

export default useCreateOrder;
